"use client";

import { createContext, useContext, useEffect, useId, useMemo } from "react";

import type { PropsWithChildren } from "react";
import type { AdvertoryPageDescriptor } from "./AdvertoryContext";

import { useAdvertory } from "./AdvertoryContext";

type AdvertoryPageContextValue = {
  id: string | null;
  active: boolean;
  mode?: AdvertoryPageDescriptor["mode"];
  creativeKey?: string;
};

const AdvertoryPageContext = createContext<AdvertoryPageContextValue>({
  id: null,
  active: true,
  mode: "page",
});

type AdvertoryPageProps = PropsWithChildren<
  Omit<AdvertoryPageDescriptor, "configuration" | "mode"> &
    Partial<Pick<AdvertoryPageDescriptor, "configuration" | "mode">>
>;

const AdvertoryPage = ({
  children,
  pageType,
  configuration = pageType === "article" ? "article" : "front",
  mode = "page",
  section,
  topic,
  article,
  keywords,
}: AdvertoryPageProps) => {
  const id = useId();
  const { activePageId, modalPlacementRevision, registerPage, unregisterPage } =
    useAdvertory();
  const descriptor = useMemo(
    () => ({
      pageType,
      configuration,
      mode,
      section,
      topic,
      article,
      keywords,
    }),
    [article, configuration, keywords, mode, pageType, section, topic],
  );

  useEffect(() => {
    registerPage(id, descriptor);
    return () => unregisterPage(id);
  }, [descriptor, id, registerPage, unregisterPage]);

  return (
    <AdvertoryPageContext.Provider
      value={{
        id,
        active: activePageId === id,
        mode,
        creativeKey:
          mode === "modal"
            ? `${article?.id}:${modalPlacementRevision}`
            : undefined,
      }}
    >
      {mode === "modal" ? (
        <div data-advertory-modal={id} style={{ display: "contents" }}>
          {children}
        </div>
      ) : (
        children
      )}
    </AdvertoryPageContext.Provider>
  );
};

const useAdvertoryPage = () => useContext(AdvertoryPageContext);

export { AdvertoryPage, AdvertoryPageContext, useAdvertoryPage };
